import { useCallback } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { BookingAnalyticsEvent } from "@/constants/analytics/Analytics";
import { BookingStepBaseTestIDMap } from "@/constants/Booking";
import { useSchedulePermissionsContext } from "@/context-providers/SchedulePermissionsContext";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import useBookingNavigation from "@/hooks/useBookingNavigation";
import { useAppSelector } from "@/store/hooks";
import {
  useAppUserPatientsInAdultProgramSelector,
  useAppUserProxyPatientsInTreatmentSelector,
} from "@/store/reducers/appUser";
import { selectPatientId, useBookingDispatch } from "@/store/reducers/booking";
import AppointmentAvatarCard from "@/ui/components/schedule/booking/AppointmentAvatarCard";
import BookingModalFooter from "@/ui/components/schedule/booking/BookingModalFooter";
import type { BaseBookingStepProps } from "@/ui/components/schedule/booking/BookingStepMap";
import BookingStepTitle from "@/ui/components/schedule/booking/BookingStepTitle";
import { FlatList } from "@/ui/layout";
import { linkedPatientToUserAvatar } from "@/utils/user.util";

const AppointmentPatientStep = ({ step }: BaseBookingStepProps) => {
  const adultPatientsForGeneralSupport =
    useAppUserPatientsInAdultProgramSelector();
  const proxyPatients = useAppUserProxyPatientsInTreatmentSelector();

  const { isBookingPermissionsFetched, patientIdsWithBookingPermissions } =
    useSchedulePermissionsContext();

  // Helper function to check if patient has booking permissions
  const isPatientWithPermissions = (patient: { externalId: string }) => {
    if (isNil(patientIdsWithBookingPermissions)) return false;
    return patientIdsWithBookingPermissions.includes(patient.externalId);
  };

  const filteredProxyPatients = isNil(proxyPatients)
    ? []
    : proxyPatients.filter(isPatientWithPermissions);

  const filteredAdultPatients = isNil(adultPatientsForGeneralSupport)
    ? []
    : adultPatientsForGeneralSupport.filter(isPatientWithPermissions);

  const patients: BaseUserAvatarProps[] = Object.values(
    [
      ...filteredProxyPatients.map(linkedPatientToUserAvatar),
      ...filteredAdultPatients.map(linkedPatientToUserAvatar),
    ].reduce(
      // eslint-disable-next-line max-params
      (acc, patient) => ({
        ...acc,
        [patient.userId]: patient,
      }),
      {},
    ),
  );
  const { trackEvent } = useBookingAnalytics();
  const { setSelectedPatientId } = useBookingDispatch();
  const selectedPatientId = useAppSelector(selectPatientId);
  const { onForwardNavigation } = useBookingNavigation({
    currentStep: step,
  });

  const handlePatientSelect = (patientId: string) => {
    if (patientId !== selectedPatientId) {
      trackEvent({
        event: BookingAnalyticsEvent.PatientSelection,
        source: step,
      });
    }
    setSelectedPatientId(patientId);
  };

  const handleForwardNavigation = () => {
    onForwardNavigation();
  };

  const baseTestID = BookingStepBaseTestIDMap[step];

  const ListHeaderComponent = useCallback(
    () => <BookingStepTitle step={step} />,
    [step],
  );

  return (
    <>
      <FlatList
        data={patients}
        isDataLoading={!isBookingPermissionsFetched}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item }) => {
          const isSelected = selectedPatientId === item.userId;
          return (
            <AppointmentAvatarCard
              isSelected={isSelected}
              onSelect={handlePatientSelect}
              userId={item.userId}
              userName={item.userName}
              userType={item.userType}
            />
          );
        }}
        testID={baseTestID}
      />
      <BookingModalFooter
        isFirstStep
        isForwardNavigationDisabled={isEmpty(selectedPatientId)}
        onForwardPress={handleForwardNavigation}
        step={step}
      />
    </>
  );
};

export default AppointmentPatientStep;
